<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@okta/okta-vue';
import authService from '@/services/AuthService';
import { ensureCapabilitiesLoaded } from '@/composables/useCapabilities';
import { ensureSelectedBrandLoaded } from '@/composables/useSelectedBrand';
import CompanyLogo from '@/components/CompanyLogo.vue';
import KHKNotificationCard from '@/components/ui/KHKNotificationCard.vue';

const router = useRouter();
const $auth = useAuth();

const loading = ref(true);
const errorMessage = ref('');

onMounted(async () => {
    try {
        await $auth.handleLoginRedirect();

        // Sync Okta SDK tokens into Vuex and start listening for renewals
        authService.syncTokensFromOkta();
        authService.startTokenSync();

        // Force-refresh RBAC capabilities for this admin — a previous admin's set
        // may be persisted in localStorage (issue #1878).
        await ensureCapabilitiesLoaded(true);

        // Force-refresh the selected brand from the backend for the same reason —
        // a previous admin's brand may be persisted in localStorage (#2122).
        await ensureSelectedBrandLoaded(true);

        console.log('Authentication successful, redirecting to dashboard');
        router.push({ name: 'dashboard' });
    } catch (error) {
        console.error('OAuth callback error:', error);
        errorMessage.value = error.message || 'Authentication failed. Please try again.';
        loading.value = false;
    }
});
</script>

<template>
    <div class="auth-screen flex items-center justify-center min-h-screen min-w-[100vw] overflow-hidden bg-surface-ground">
        <div class="flex flex-col items-center justify-center">
            <div
                style="border-radius: 56px; padding: 0.3rem; background: linear-gradient(180deg, var(--primary-color) 10%, rgba(33, 150, 243, 0) 30%)">
                <div class="w-full bg-surface-card py-20 px-8 sm:px-20" style="border-radius: 53px">
                    <div class="text-center mb-8 flex flex-col justify-center items-center gap-4">
                        <div class="auth-logo">
                            <CompanyLogo />
                        </div>
                    </div>

                    <div class="w-full max-w-md text-center">
                        <KHKNotificationCard
                            v-if="errorMessage"
                            :notification="{ message: errorMessage }"
                            type="error"
                            class="mb-6 md:w-[30rem]"
                        />

                        <div v-if="loading" class="flex flex-col items-center gap-4">
                            <i class="pi pi-spinner pi-spin text-4xl text-primary"></i>
                            <p class="text-surface-600 dark:text-surface-400">
                                Completing authentication...
                            </p>
                        </div>

                        <div v-else-if="errorMessage" class="flex flex-col items-center gap-4">
                            <router-link
                                :to="{ name: 'login' }"
                                class="text-primary hover:underline"
                            >
                                Return to login
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

